/**
 * Utility for creating Electron plugin proxies.
 * These plugins communicate with Electron's main process via IPC
 * through the CapacitorCustomPlatform bridge exposed by the preload script.
 */

import { isElectron } from '../shared/utils/platform.utils';

interface PluginConfig<T> {
    web?: () => T;
}

type PluginMethod = (...args: unknown[]) => Promise<unknown>;

interface PluginInstance {
    [method: string]: PluginMethod;
}

/**
 * Gets the Electron plugin instance from the preload bridge
 */
function getElectronPlugin(pluginName: string): PluginInstance | undefined {
    return (window as { CapacitorCustomPlatform?: { plugins?: Record<string, PluginInstance> } }).CapacitorCustomPlatform?.plugins?.[pluginName];
}

/**
 * Creates a type-safe proxy for an Electron plugin.
 * Uses JavaScript Proxy to dynamically forward method calls to the native plugin.
 *
 * @param pluginName - The name of the plugin as registered in the preload script
 * @param config - Optional configuration with web fallback class factory
 *
 * @example
 * // Basic usage - Electron only
 * const MyPlugin = createElectronPlugin<MyPluginInterface>('MyPlugin');
 *
 * @example
 * // With web fallback class
 * const MyPlugin = createElectronPlugin<MyPluginInterface>('MyPlugin', {
 *     web: () => new MyPluginWeb()
 * });
 */
export function createElectronPlugin<T extends object>(
    pluginName: string,
    config?: PluginConfig<T>
): T {
    let webInstance: T | null = null;

    return new Proxy({} as T, {
        get(_, method: string) {
            return async (...args: unknown[]) => {
                // If in Electron, use native plugin
                if (isElectron()) {
                    const plugin = getElectronPlugin(pluginName);
                    if (!plugin) {
                        const available = Object.keys(
                            (window as { CapacitorCustomPlatform?: { plugins?: Record<string, unknown> } })
                                .CapacitorCustomPlatform?.plugins ?? {}
                        );
                        throw new Error(
                            `Electron plugin "${pluginName}" is not available. ` +
                            `Available plugins: [${available.join(', ')}]`
                        );
                    }
                    if (typeof plugin[method] !== 'function') {
                        throw new Error(`Method "${method}" does not exist on plugin "${pluginName}"`);
                    }
                    return plugin[method](...args);
                }

                // If web fallback factory exists, instantiate and use it
                if (config?.web) {
                    if (!webInstance) {
                        webInstance = config.web();
                    }
                    const webMethod = (webInstance as Record<string, PluginMethod>)[method];
                    if (typeof webMethod === 'function') {
                        return webMethod(...args);
                    }
                    throw new Error(`Method "${method}" does not exist on web plugin "${pluginName}"`);
                }

                // No implementation available
                throw new Error(`Plugin "${pluginName}" is not available on this platform`);
            };
        }
    });
}

/**
 * Checks if Electron plugins are available
 */
export function hasElectronPlugins(): boolean {
    return isElectron() && !!(window as { CapacitorCustomPlatform?: { plugins?: unknown } }).CapacitorCustomPlatform?.plugins;
}
